/**
 * InsightsGeneratorHelper
 * 
 * Static helper methods for generating markdown content for insights/AI integration.
 * 
 * Methods:
 * 1. buildSnapshotMarkdownForAPI() - Builds markdown from OutputData for snapshot API
 * 2. buildCrosstabMarkdownForAPI() - Builds markdown from CrossTabOutPutData for crosstab API
 * 3. buildSnapshotMarkdownFromWidget() - Builds markdown from widget data structure
 * 4. buildMarkdownPayload() - Builds API payload for insights requests
 */

export class InsightsGeneratorHelper {
  static hasValidDataInMarkdown(markdown: string): boolean {
    if (!markdown) return false;
    const lines = markdown.split('\n');
    for (const line of lines) {
      const tLine = line.trim();
      if (tLine === '' || tLine.startsWith('###') || tLine.startsWith('| Metric') || tLine.startsWith('| ---')) {
        continue;
      }
      if (tLine.startsWith('|')) {
        const cells = tLine.split('|').map(c => c.trim());
        // Remove first empty split (before first |) and last empty split (after last |)
        if (cells.length > 0 && cells[0] === '') cells.shift();
        if (cells.length > 0 && cells[cells.length - 1] === '') cells.pop();
        
        // First cell is metric name, check remaining cells
        for (let i = 1; i < cells.length; i++) {
          if (cells[i] !== 'NA' && cells[i] !== 'I/S' && cells[i] !== '-') {
            return true;
          }
        }
      } else if (tLine.includes(':')) {
        const parts = tLine.split(':');
        // Handle cases where metric name might have a colon, we take the last part as value
        const val = parts[parts.length - 1].trim();
        if (val !== 'NA' && val !== 'I/S' && val !== '-') {
          return true;
        }
      }
    }
    return false;
  }

  static buildSnapshotMarkdownForAPI(
    OutputData: any[],
    isTotalBase: boolean,
    selectedWeight: string,
    insufficientBase: number = 0
  ): string {
    if (!OutputData || OutputData.length === 0) {
      return '### Snapshot Data\n\nNo data available';
    }
    try {
      const widgets = OutputData.reduce((acc: any, item: any) => {
        const widgetName = item.widgetName || 'Analysis';
        if (!acc[widgetName]) {
          acc[widgetName] = [];
        }
        acc[widgetName].push(item);
        return acc;
      }, {});
      let markdown = '';
      Object.keys(widgets).forEach(widgetName => {
        markdown += `### ${widgetName}\n\n`;
        markdown += '| Metric | Value |\n';
        markdown += '| --- | --- |\n';
        widgets[widgetName].forEach((item: any) => {
          const metric = item.rowChild || 'Metric';
          let value = '';
          const baseValue = isTotalBase ? item.totalUnweightedBase : item.answerUnweightedBase;
          if (baseValue === 0 || baseValue === null || baseValue === undefined) {
            value = 'NA';
          } else if (insufficientBase > 0 && baseValue < insufficientBase) {
            value = 'I/S';
          } else {
            if (isTotalBase) {
              value = selectedWeight === "Unweighted"
                ? `${((item.totalUnweightedShare || 0) * 100).toFixed(1)}%`
                : `${((item.totalWeightedShare || 0) * 100).toFixed(1)}%`;
            } else {
              value = selectedWeight === "Unweighted"
                ? `${((item.answerUnweightedShare || 0) * 100).toFixed(1)}%`
                : `${((item.answerWeightedShare || 0) * 100).toFixed(1)}%`;
            }
          }
          markdown += `| ${metric} | ${value} |\n`;
        });
        markdown += '\n';
      });
      return markdown;
    } catch (error) {
      console.error('Error building snapshot markdown:', error);
      return '### Snapshot Data\n\nError generating markdown';
    }
  }

  static buildCrosstabMarkdownForAPI(
    CrossTabOutPutData: any[],
    isTotalBase: boolean,
    selectedWeight: string,
    insufficientBase: number = 0
  ): string {
    if (!CrossTabOutPutData || CrossTabOutPutData.length === 0) {
      return '### Analysis Data\n\nNo data available';
    }
    try {
      let markdown = '### Analysis Results\n\n';
      const metrics = [...new Set(CrossTabOutPutData.map((item: any) => item.rowChild))];
      let brands = [...new Set(CrossTabOutPutData.map((item: any) => item.colChild))];
      const namedBrands = brands.filter(b => b !== null && b !== undefined && b !== '');
      if (namedBrands.length > 0) {
        brands = namedBrands;
      }
      
      if (brands.length > 0) {
        markdown += '| Metric | ' + brands.join(' | ') + ' |\n';
        markdown += '| --- | ' + brands.map(() => '---').join(' | ') + ' |\n';
        metrics.forEach(metric => {
          let row = `| ${metric} |`;
          brands.forEach(brand => {
            const item = CrossTabOutPutData.find((d: any) =>
              d.rowChild === metric && d.colChild === brand
            );
            if (item) {
              let value = '';
              const baseValue = isTotalBase ? item.totalUnweightedBase : item.answerUnweightedBase;
              if (baseValue === 0 || baseValue === null || baseValue === undefined) {
                value = 'NA';
              } else if (insufficientBase > 0 && baseValue < insufficientBase) {
                value = 'I/S';
              } else {
                if (isTotalBase) {
                  value = selectedWeight === "Unweighted"
                    ? `${((item.totalUnweightedShare || 0) * 100).toFixed(1)}%`
                    : `${((item.totalWeightedShare || 0) * 100).toFixed(1)}%`;
                } else {
                  value = selectedWeight === "Unweighted"
                    ? `${((item.answerUnweightedShare || 0) * 100).toFixed(1)}%`
                    : `${((item.answerWeightedShare || 0) * 100).toFixed(1)}%`;
                }
              }
              row += ` ${value} |`;
            } else {
              row += ' - |';
            }
          });
          markdown += row + '\n';
        });
      } else {
        metrics.forEach(metric => {
          const item = CrossTabOutPutData.find((d: any) => d.rowChild === metric);
          if (item) {
            let value = '';
            const baseValue = isTotalBase ? item.totalUnweightedBase : item.answerUnweightedBase;
            if (baseValue === 0 || baseValue === null || baseValue === undefined) {
              value = 'NA';
            } else if (insufficientBase > 0 && baseValue < insufficientBase) {
              value = 'I/S';
            } else {
              if (isTotalBase) {
                value = selectedWeight === "Unweighted"
                  ? `${((item.totalUnweightedShare || 0) * 100).toFixed(1)}%`
                  : `${((item.totalWeightedShare || 0) * 100).toFixed(1)}%`;
              } else {
                value = selectedWeight === "Unweighted"
                  ? `${((item.answerUnweightedShare || 0) * 100).toFixed(1)}%`
                  : `${((item.answerWeightedShare || 0) * 100).toFixed(1)}%`;
              }
            }
            markdown += `**${metric}**: ${value}\n\n`;
          }
        });
      }
      return markdown;
    } catch (error) {
      console.error('Error building crosstab markdown:', error);
      return '### Analysis Data\n\nError generating markdown';
    }
  }

  static buildSnapshotMarkdownFromWidget(
    filteredWidgetData: any
  ): string {
    try {
      const widgetData = filteredWidgetData;
      const metricValues: any[] = widgetData?.metric?.[0]?.metric_value || [];
      if (!metricValues.length) {
        console.warn('[Snapshot Markdown] metric_value is empty or unavailable.');
        return '';
      }
      const colChildSet = new Set<string>();
      metricValues.forEach((entry: any) => {
        (entry.data || []).forEach((d: any) => {
          if (d.colChild) colChildSet.add(d.colChild);
        });
      });
      const colChildren: string[] = Array.from(colChildSet);
      const displayCols: string[] = colChildren.map((c: string) =>
        c.includes('|') ? c.split('|').pop()! : c
      );
      let md = `### ${widgetData.widget_name || 'Snapshot'}\n\n`;
      md += `| Metric | ${displayCols.join(' | ')} |\n`;
      md += `| --- | ${displayCols.map(() => '---').join(' | ')} |\n`;
      metricValues.forEach((entry: any) => {
        const rawLabel: string = entry.option || '';
        const label = rawLabel.includes('|') ? rawLabel.split('|').pop()!.trim() : rawLabel;
        const dataArr: any[] = entry.data || [];
        const rowVals = colChildren.map((colKey: string) => {
          const d = dataArr.find((x: any) => x.colChild === colKey);
          if (!d) return 'NA';
          const raw = d.data ?? d.totalWeightedShare ?? d.totalUnweightedShare
            ?? d.answerWeightedShare ?? d.answerUnweightedShare ?? null;
          if (raw === null || raw === undefined) return 'NA';
          const num = Number(raw);
          return isNaN(num) ? 'NA' : `${(num * 100).toFixed(1)}%`;
        });
        md += `| ${label} | ${rowVals.join(' | ')} |\n`;
      });
      return md;
    } catch (err) {
      console.error('[Snapshot Markdown] Error:', err);
      return '';
    }
  }

  static buildTrendSnapshotMarkdownFromWidget(
    filteredWidgetData: any
  ): string {
    try {
      const trendDataStr = filteredWidgetData?.trendline_response?.Data;
      if (!trendDataStr) return '';

      const trendData = typeof trendDataStr === 'string' ? JSON.parse(trendDataStr) : trendDataStr;
      if (!Array.isArray(trendData) || trendData.length === 0) return '';

      // Get unique time periods (columns) - usually Level1ColChild represents the trend period
      const periods = [...new Set(trendData.map((item: any) => item.Level1ColChild))].sort();
      const displayCols = periods.map((p: any) => p.includes('|') ? p.split('|').pop()! : p);

      // Get unique metrics (rows)
      const metrics = [...new Set(trendData.map((item: any) => item.rowChild))];

      let md = `### ${filteredWidgetData.widget_name || 'Snapshot'} - Trend Analysis\n\n`;
      md += `| Metric | ${displayCols.join(' | ')} |\n`;
      md += `| --- | ${displayCols.map(() => '---').join(' | ')} |\n`;

      metrics.forEach(metric => {
        let row = `| ${metric} |`;
        periods.forEach(period => {
          const item = trendData.find((d: any) => d.rowChild === metric && d.Level1ColChild === period);
          if (item) {
            const raw = item.data ?? item.totalWeightedShare ?? item.totalUnweightedShare
              ?? item.answerWeightedShare ?? item.answerUnweightedShare ?? null;
            if (raw === null || raw === undefined) {
              row += ' NA |';
            } else {
              const num = Number(raw);
              row += isNaN(num) ? ' NA |' : ` ${(num * 100).toFixed(1)}% |`;
            }
          } else {
            row += ' NA |';
          }
        });
        md += row + '\n';
      });

      console.log('[Trend Markdown] Generated:', { length: md.length });
      return md;
    } catch (err) {
      console.error('[Snapshot Trend Markdown] Error:', err);
      return '';
    }
  }

  static buildMarkdownPayload(params: {
    token: string;
    mcode: number;
    selected_client: string;
    landingdata: any;
    selected_study: string;
    snapshotinsights_payload: any;
    insights_payload: any;
    guidChatId: string;
    inputValue: string;
    filteredWidgetData?: any;
    filteredWidgetID?: string;
    Snapshotdataforexport?: any;
    selectedSnapshotType?: string;
    selectedSection?: string;
  }): any {
    try {
      const isSnapshot = params.mcode === 1;
      const basePayload: any = {
        client_name: params.selected_client || params.landingdata?.client_code || '',
        study: params.selected_study || params.landingdata?.study || '',
        mcode: params.mcode,
        markdown: isSnapshot ? params.snapshotinsights_payload : params.insights_payload,
        jwttoken: params.token,
        sessionid: params.guidChatId,
        query: params.inputValue
      };
      if (isSnapshot && params.filteredWidgetData) {
        basePayload['widget_details'] = {
          widget_name: params.filteredWidgetData.widget_name || '',
          widget_id: params.filteredWidgetID || '',
          widgetchart_name: params.filteredWidgetData.widgetchart_name || '',
          Snapshot_Name: params.Snapshotdataforexport?.snapshotName || '',
          Snapshot_Type: params.selectedSnapshotType || '',
          Section: params.selectedSection
        };
      }
      return basePayload;
    } catch (err) {
      console.error('[Markdown Payload] Error building payload:', err);
      return null;
    }
  }
}
